import React, { FC, useEffect, useMemo } from 'react';
import { RouteComponentProps, useNavigate } from '@reach/router';
import { useTranslation } from 'react-i18next';

import { useCloverPaymentIframeContext } from 'hooks/context/cloverPaymentIframe';
import { useCheckoutSessionContext } from 'hooks/context/checkoutSession';
import { useMerchantPaymentConfigContext } from 'hooks/context/merchantPaymentConfig';
import { useCalcContext } from 'hooks/context/calc';

import { Card } from 'components/Card';
import { PageLayout } from 'components/PageLayout';
import { Header } from 'components/PageLayout/Header';
import { InvoiceSummary } from 'components/InvoiceSummary';
import { isCheckoutSessionExpired, isCheckoutSessionPaid, checkoutSessionMaxPayAttemptsReached, isCheckoutSessionCancelled } from 'services/checkout-service/helpers';
import { extractMerchantBusinessLogo } from 'utils/Logo';
import { CheckoutType } from 'services/checkout-service/models';
import { CLIENT_PATHS } from 'services/checkout-service/axiosInstance';

import { CheckoutDetailsShimmer } from './CheckoutDetailsShimmer';
import { CloverPaymentSection } from './CloverPaymentSection';
import { LineItems } from './LineItems';
import { Totals } from './Totals';
import { CHECKOUT_DETAILS_TEST_IDS } from './const';

import styles from './styles.module.scss';

const CheckoutDetails: FC<RouteComponentProps<{checkoutSessionId: Uuid}>> = ({ checkoutSessionId }) => {
  const { t } = useTranslation();
  const { isLoading, checkoutSession, error, merchantSettings } = useCheckoutSessionContext();
  const { merchantPaymentConfig } = useMerchantPaymentConfigContext();
  const { setLocale, locale, merchantUuid, setMerchantUuid } = useCloverPaymentIframeContext();
  const merchantLocale = checkoutSession?.merchant?.properties?.locale;

  const navigate = useNavigate();

  const { shoppingCart, type, additionalCharges } = checkoutSession || {};

  const { subtotal, totalTaxAmount, lineItems } = shoppingCart || {};
  const { payableTotalAmount, tipAmount, minAmount } = useCalcContext();

  useEffect(() => {
    // If a merchant locale is already set don't reset it (re-trigger iframe render)
    if (locale) return;
    merchantLocale && setLocale(merchantLocale);
  }, [locale, merchantLocale, setLocale]);

  useEffect(() => {
    // If a merchant uuid is already set dont reset it (re-trigger iframe render)
    if (merchantUuid || !checkoutSession) return;

    const checkoutSessionMerchantUuid = checkoutSession.merchant.id;
    checkoutSessionMerchantUuid && setMerchantUuid(checkoutSessionMerchantUuid);
  }, [merchantUuid, checkoutSession, setMerchantUuid]);

  useEffect(() => {
    if (isCheckoutSessionPaid(checkoutSession?.status)) {
      navigate(`/${CLIENT_PATHS[0]}/${checkoutSessionId}/confirmation`, {
        replace: true,
      });
    }
  }, [checkoutSession, checkoutSessionId, navigate]);

  const isPayWidget = useMemo(() => type === CheckoutType.PAY_WIDGETS, [type]);

  const isSurchargeSupported = useMemo(() => merchantPaymentConfig?.surcharging?.supported ?? false, [merchantPaymentConfig]);

  const computeTotalAmount = useMemo(() => {
    let tempAmount = 0;
    for (const additionalCharge of additionalCharges || []) {
      const { type, amount } = additionalCharge;
      if ((type === 'CONVENIENCE_FEE' && isSurchargeSupported) || (type === 'CREDIT_SURCHARGE' && !isSurchargeSupported)) continue;
      tempAmount += amount;
    }

    return tempAmount + tipAmount + payableTotalAmount;
  }, [additionalCharges, isSurchargeSupported, tipAmount, payableTotalAmount]);

  if (isLoading) return <CheckoutDetailsShimmer />;

  if (error) {
    console.error('Error fetching checkout session');
    throw new Error(t('errorLoadingPage'));
  }

  if (!checkoutSession) return null;

  const { merchant: { name, logos }, invoice, customer, status, createdTime } = checkoutSession;

  const { id, notes } = invoice || {};
  const isInvoiceMode = !!invoice;

  const checkoutSessionExpired = !isPayWidget && isCheckoutSessionExpired(status);

  const hasMaxPayAttemptsReached = !isPayWidget && checkoutSessionMaxPayAttemptsReached(status);

  const checkoutSessionCancelled = !isPayWidget && isCheckoutSessionCancelled(status);

  const { BUSINESS_NAME, HEADER_COLOR, BACKGROUND_COLOR, FOOTER_COLOR, FOOTER_TEXT, LOGO_SIZE } = merchantSettings || {};

  return (
    <PageLayout
      customBodyColor={BACKGROUND_COLOR}
      customFooterColor={FOOTER_COLOR}
      customFooterText={FOOTER_TEXT}
      data-testid={CHECKOUT_DETAILS_TEST_IDS.root}
      header={
        <Header
          customHeaderColor={HEADER_COLOR}
          isInvoiceMode={isInvoiceMode}
          merchantName={(BUSINESS_NAME && !isInvoiceMode) ? BUSINESS_NAME : name}
          merchantLogo={extractMerchantBusinessLogo(logos)}
          logoSize={LOGO_SIZE}
        />
      }
      isInvoiceMode={isInvoiceMode}
    >
      <Card>
        <div className={styles.headingWrapper}>
          <h1 data-testid={CHECKOUT_DETAILS_TEST_IDS.heading}>
            {invoice ? t('invoiceRequest') : t('yourOrder')}
          </h1>
          {checkoutSessionExpired &&
            <span data-testid={CHECKOUT_DETAILS_TEST_IDS.expiredTag} className={styles.expiredTag}>
              {t('expired')}
            </span>
          }
          {checkoutSessionCancelled &&
            <span data-testid={CHECKOUT_DETAILS_TEST_IDS.cancelledTag} className={styles.expiredTag}>
              {t('cancelled')}
            </span>
          }
        </div>

        {invoice
          ? <InvoiceSummary notes={notes} id={id} createdTime={createdTime} />
          : <LineItems lineItems={lineItems} />
        }
        <Totals
          subtotal={subtotal || payableTotalAmount}
          taxAmount={totalTaxAmount}
          total={computeTotalAmount}
          tip={tipAmount}
        />

        {!checkoutSessionExpired && !checkoutSessionCancelled &&
          <section data-testid={CHECKOUT_DETAILS_TEST_IDS.paymentSection}>
            <CloverPaymentSection
              checkoutSessionId={checkoutSessionId}
              total={computeTotalAmount}
              hasMaxPayAttemptsReached={hasMaxPayAttemptsReached}
              isInvoiceMode={isInvoiceMode}
              isPayWidget={isPayWidget}
              customer={customer}
              minAmount={minAmount}
              payWidgetAmount={payableTotalAmount}
              subtotal={payableTotalAmount}
            />
          </section>
        }
      </Card>
    </PageLayout>
  );
};

export { CheckoutDetails };
